import React, { useEffect, useRef, useState } from "react";

/**
 * CodeRain — the falling-code field behind the calm bands of the site.
 *
 * Columns of 0s and 1s fall on a fixed grid, mixed through the brand blues rather than one flat
 * navy: on the pale bands, ink navy with Connexis blue and the pale blue threaded through it; on
 * the navy bands, near-white with the same two blues. The leading character of every column is
 * the brightest thing in it, which is what makes the field read as *falling* rather than printed.
 * Roughly one glyph in twenty is the Connexis mark drawn as vector strokes.
 *
 * It behaves itself: paused when the band is off-screen or the tab is hidden, frame-rate capped
 * (a "whisper" field repaints 14 times a second, not 60), thinned out on a phone — wider columns,
 * a shorter trail and half the frame rate, so a 6" screen doesn't turn into porridge or eat the
 * battery — and it draws nothing at all for anyone whose system asks for reduced motion.
 */
const PRESETS = {
  whisper:  { col: 30, row: 17, glyph: 11,   trail: 11, speed: 1.7, fps: 14, head: 0.52, tail: 0.24, logo: 0.075 },
  balanced: { col: 25, row: 16, glyph: 12,   trail: 13, speed: 2.5, fps: 18, head: 0.68, tail: 0.32, logo: 0.095 },
  showcase: { col: 21, row: 15, glyph: 12.5, trail: 18, speed: 3.4, fps: 22, head: 0.88, tail: 0.46, logo: 0.125 },
};

// [ink, connexis blue, pale blue] — weights decide how much colour is threaded through
const THEMES = {
  light: {
    tones: ["15, 23, 42", "0, 85, 255", "125, 169, 255"],
    weights: [0.58, 0.24, 0.18],
    headWeights: [0.42, 0.36, 0.22],
    boost: [1, 0.92, 1.15],
  },
  dark: {
    tones: ["226, 235, 255", "77, 140, 255", "125, 169, 255"],
    weights: [0.34, 0.26, 0.40],
    headWeights: [0.52, 0.28, 0.20],
    boost: [1, 1.1, 1],
  },
};

const bit = () => (Math.random() > 0.5 ? "1" : "0");

const pick = (weights) => {
  const r = Math.random();
  let acc = 0;
  for (let i = 0; i < weights.length; i++) {
    acc += weights[i];
    if (r <= acc) return i;
  }
  return 0;
};

export default function CodeRain({ variant = "light", intensity = "whisper", className = "" }) {
  const hostRef = useRef(null);
  const canvasRef = useRef(null);
  const [narrow, setNarrow] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const onChange = (e) => setNarrow(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  useEffect(() => {
    const host = hostRef.current;
    const canvas = canvasRef.current;
    if (!host || !canvas) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const base = PRESETS[intensity] || PRESETS.whisper;
    // On a phone: fewer, wider columns, a shorter trail and half the frame rate.
    const P = narrow
      ? { ...base, col: base.col * 1.5, row: base.row * 1.15, glyph: base.glyph * 0.92,
          trail: Math.max(6, Math.round(base.trail * 0.75)), speed: base.speed * 0.9,
          fps: Math.min(base.fps, 12), logo: base.logo }
      : base;
    const T = THEMES[variant] || THEMES.light;
    const ctx = canvas.getContext("2d", { alpha: true });
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const frameGap = 1000 / P.fps;

    let cols = [];
    let w = 0;
    let h = 0;
    let rows = 0;
    let raf = 0;
    let last = 0;
    let visible = true;

    const size = () => {
      const r = host.getBoundingClientRect();
      w = Math.max(1, Math.floor(r.width));
      h = Math.max(1, Math.floor(r.height));
      canvas.width = Math.floor(w * dpr);
      canvas.height = Math.floor(h * dpr);
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      rows = Math.ceil(h / P.row) + P.trail + 2;

      const n = Math.max(1, Math.round(w / P.col));
      const gap = w / n;
      cols = Array.from({ length: n }, (_, i) => ({
        x: Math.round(i * gap + gap / 2),
        // scattered on first paint so the field is already alive, never a synchronised wall
        head: Math.random() * rows,
        speed: P.speed * (0.7 + Math.random() * 0.7),
        chars: Array.from({ length: rows }, bit),
        tones: Array.from({ length: rows }, () => pick(T.weights)),
        marks: Array.from({ length: rows }, () => Math.random() < P.logo),
      }));
    };

    // The Connexis mark: three rails, two nodes — one glyph in the stream, but the one you notice.
    const drawMark = (x, y, s, alpha, toneIdx) => {
      const u = s / 24;
      ctx.save();
      ctx.translate(x - s / 2, y - s / 2);
      ctx.lineCap = "round";
      ctx.lineWidth = Math.max(1.1, 2.2 * u);
      // a breath of glow on the brighter marks, so the logo reads as a mark and not a smudge
      if (alpha > 0.3) {
        ctx.shadowColor = `rgba(${T.tones[1]}, ${Math.min(0.6, alpha)})`;
        ctx.shadowBlur = s * 0.45;
      }
      ctx.strokeStyle = `rgba(${T.tones[toneIdx]}, ${Math.min(1, alpha)})`;
      ctx.beginPath();
      ctx.moveTo(4 * u, 7 * u);  ctx.lineTo(11 * u, 7 * u);
      ctx.moveTo(4 * u, 12 * u); ctx.lineTo(14 * u, 12 * u);
      ctx.moveTo(4 * u, 17 * u); ctx.lineTo(11 * u, 17 * u);
      ctx.stroke();
      ctx.fillStyle = `rgba(${T.tones[1]}, ${Math.min(1, alpha * 1.8)})`;
      ctx.beginPath(); ctx.arc(18 * u, 7 * u,  2.4 * u, 0, Math.PI * 2); ctx.fill();
      ctx.beginPath(); ctx.arc(18 * u, 17 * u, 2.4 * u, 0, Math.PI * 2); ctx.fill();
      ctx.restore();
    };

    const tick = (t) => {
      raf = requestAnimationFrame(tick);
      if (!visible || t - last < frameGap) return;
      const dt = Math.min(0.2, (t - last) / 1000);
      last = t;

      ctx.clearRect(0, 0, w, h);
      ctx.font = `600 ${P.glyph}px ui-monospace, "SFMono-Regular", Menlo, monospace`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";

      for (const c of cols) {
        c.head += c.speed * dt * 10;
        if (c.head - P.trail > rows) {
          c.head = -Math.random() * rows * 0.6;
          c.speed = P.speed * (0.7 + Math.random() * 0.7);
          c.headTone = pick(T.headWeights);
        }
        const headRow = Math.floor(c.head);

        for (let k = 0; k < P.trail; k++) {
          const row = headRow - k;
          if (row < 0) continue;
          const y = row * P.row + P.row / 2;
          if (y < -P.row || y > h + P.row) continue;

          const idx = ((row % rows) + rows) % rows;
          const tone = k === 0 ? (c.headTone ?? pick(T.headWeights)) : c.tones[idx];
          // the head lands brightest; everything behind it fades out linearly
          const alpha = (k === 0 ? P.head : P.tail * (1 - k / P.trail)) * T.boost[tone];
          if (alpha < 0.012) continue;

          if (c.marks[idx]) {
            drawMark(c.x, y, P.glyph * 1.7, Math.min(1, alpha * 1.3), tone);
            continue;
          }
          ctx.fillStyle = `rgba(${T.tones[tone]}, ${Math.min(1, alpha)})`;
          ctx.fillText(c.chars[idx], c.x, y);
        }

        // one glyph somewhere in the column flickers each frame — the field never looks printed
        if (Math.random() < 0.25) {
          const f = Math.floor(Math.random() * rows);
          c.chars[f] = bit();
        }
      }
    };

    size();
    raf = requestAnimationFrame(tick);

    const ro = new ResizeObserver(size);
    ro.observe(host);
    const io = new IntersectionObserver(([e]) => { visible = e.isIntersecting; }, { rootMargin: "120px" });
    io.observe(host);
    const onVis = () => { visible = !document.hidden; };
    document.addEventListener("visibilitychange", onVis);

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      io.disconnect();
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [variant, intensity, narrow]);

  return (
    <div ref={hostRef} aria-hidden="true" data-testid="code-rain"
         className={`pointer-events-none absolute inset-0 overflow-hidden ${className}`}
         style={{ maskImage: "linear-gradient(to bottom, transparent, #000 10%, #000 90%, transparent)",
                  WebkitMaskImage: "linear-gradient(to bottom, transparent, #000 10%, #000 90%, transparent)" }}>
      <canvas ref={canvasRef} className="block h-full w-full" />
    </div>
  );
}
